import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_CONFIG } from '../config/seo';
import { SITE_CONFIG } from '../config';

const absoluteUrl = (path = '/') => {
  if (/^https?:\/\//.test(path)) return path;
  const normalized = path === '/' ? '' : `/${path.replace(/^\/+|\/+$/g, '')}`;
  return `${SEO_CONFIG.siteUrl}${normalized}`;
};

const SEOHelmet = ({
  title = SEO_CONFIG.defaultTitle,
  description = SEO_CONFIG.defaultDescription,
  path = '/',
  image = SEO_CONFIG.defaultImage,
  type = 'website',
  noindex = false,
  schema = [],
  breadcrumbs = []
}) => {
  const canonical = absoluteUrl(path);
  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': ['HomeAndConstructionBusiness', 'GeneralContractor'],
      '@id': `${SEO_CONFIG.siteUrl}/#business`,
      name: SITE_CONFIG.BUSINESS.LEGAL_NAME,
      alternateName: SITE_CONFIG.BUSINESS.NAME,
      url: SEO_CONFIG.siteUrl,
      logo: SITE_CONFIG.ASSETS.LOGO,
      image: SITE_CONFIG.ASSETS.TEAM,
      telephone: SITE_CONFIG.BUSINESS.PHONE_E164,
      email: SITE_CONFIG.BUSINESS.EMAIL,
      priceRange: '$$',
      address: { '@type': 'PostalAddress', ...SEO_CONFIG.business.address },
      areaServed: { '@type': 'City', name: 'Maricopa, Arizona' },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '16:30' }
      ],
      sameAs: [SITE_CONFIG.BUSINESS.FACEBOOK]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SEO_CONFIG.siteUrl}/#website`,
      url: SEO_CONFIG.siteUrl,
      name: SITE_CONFIG.BUSINESS.NAME,
      inLanguage: 'en-US',
      publisher: { '@id': `${SEO_CONFIG.siteUrl}/#business` }
    },
    ...(breadcrumbs.length ? [{
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: absoluteUrl(item.path)
      }))
    }] : []),
    ...schema
  ];

  return (
    <>
    <Helmet>
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <link rel="canonical" href={canonical} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_CONFIG.BUSINESS.NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={absoluteUrl(image)} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteUrl(image)} />
    </Helmet>
    {schemas.map((item, index) => (
      <script type="application/ld+json" key={index} dangerouslySetInnerHTML={{ __html: JSON.stringify(item).replace(/</g, '\\u003c') }} />
    ))}
    </>
  );
};

export default SEOHelmet;
